import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    AlertTriangle, ArrowLeft, CalendarRange, CheckCircle2, Clock, Download, FolderOpen,
    Grid3x3, HelpCircle, List, RefreshCw,
} from 'lucide-react';
import { DokumentePanel } from '../dokumente/DokumentePanel';
import { holdIdleExit } from '../../services/idleHold';
import { readOnlyMessage, readOnlyTitle } from '../../services/readOnlyNotice';
import { buildCompanyForPdf } from '../../services/pdf/companyForPdf';
import {
    generateKoordinierungPdf, heuteAlsIso, koordinierungDateiname,
} from '../../services/pdf/generatorKoordinierung';
import {
    bestaetigeKoordinierungPaar,
    createKoordinierungPaar,
    deleteKoordinierungPaar,
    hebeVeralterungAuf,
    markiereVeraltet,
    uebernimmNeuesFenster,
    updateKoordinierungPaar,
    widerrufeBestaetigung,
    type Schreibzugriff,
} from '../../services/koordinierungService';
import { baueKoordinierungKontext } from '../../services/aiAssistContext';
import { ladePlanungsphaseFuerKi } from '../../services/aiAssistPlanung';
import type { KiBruecke, KiOeffnung, KiUebernahme } from '../sige-plan/kiAssistentLogic';
import { planHatZeitachse } from '../sige-plan/types';
import { GewerkZuordnung } from './GewerkZuordnung';
import { MatrixAnsicht } from './MatrixAnsicht';
import { PaarDetail } from './PaarDetail';
import { ZeitachseAnsicht } from './ZeitachseAnsicht';
import { useKoordinierung } from './useKoordinierung';
import {
    aktuelleKatalogVersion,
    filterZeilen,
    leeresPaar,
    paarAusVorschlag,
    sortiereZeilen,
    type AbgleichZeile,
    type GespeicherteBewertung,
    type KoordinierungPaar,
    type PaarStatus,
    type Prioritaet,
    type Vorschlag,
} from './koordinierungLogic';

/**
 * Props zeichengleich zu `PlanungModul` — auch die Felder, die dieses Modul
 * nicht selbst liest. Die Aufrufstelle in `App.tsx` wurde in AP0 geschrieben
 * und wird hier nicht angefasst.
 */
interface Props {
    projectId: string;
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    userProfile?: any;
    /** Firmenstammdaten aus dem Profil (App.invoiceCompany). */
    company?: unknown;
    /** Briefkopf-Konfiguration (App.quoteConfigMemo). */
    quoteConfig?: unknown;
    /** Rechnungs-Stammdaten fuer die Fusszeile (App.invoiceConfigMemo). */
    invoiceConfig?: unknown;
    onBack: () => void;
    /**
     * Meldet dem KI-Assistenzfenster der Huelle, was dieses Modul beisteuert:
     * den Kontext seiner Lage und den Weg, einen angenommenen Vorschlag
     * anzulegen. `null` beim Verlassen — dann hat das Fenster nichts zu zeigen.
     *
     * Fehlt die Prop, verhaelt sich das Modul wie zuvor; der Assistent ist ein
     * Zusatz und keine Voraussetzung.
     */
    onKiBruecke?: (bruecke: KiBruecke | null) => void;
    isReadOnly?: boolean;
}

type Reiter = 'matrix' | 'zeitachse' | 'liste' | 'gewerke' | 'dokumente';

/**
 * Gewerke-Ueberschneidung (Koordinierung nach § 3 BaustellV) — duenner
 * Container.
 *
 * Er laedt die Lage, schaltet zwischen den Ansichten und reicht Knopfdruecke an
 * den Dienst weiter. Jede Regel, die man pruefen kann, steht daneben in
 * `koordinierungLogic.ts` (stehende Entscheidung „duenner Container, Logik in
 * Hooks und puren Funktionen"; es gibt kein jsdom, Komponenten sind nicht
 * testbar).
 *
 * ═══ ZWEI REIHEN SCHREIBSCHUTZ ═══════════════════════════════════════════
 * `isReadOnly` schaltet die Knoepfe ab UND wird in jedem Schreibweg noch einmal
 * geprueft — hier ueber `abgelehntWennNurAnsicht`, im Dienst ein zweites Mal
 * ueber den `Schreibzugriff`. Das ist kein Guertel-und-Hosentraeger: das Modul
 * haengt an einer eigenen Tabelle ohne `handleUpdateReport`-Wachposten, und
 * `isReadOnly` kann zur Laufzeit kippen, wenn die Projektsperre an einen
 * anderen Benutzer geht.
 *
 * ═══ WAS DIESES MODUL NICHT TUT ══════════════════════════════════════════
 * Es schreibt nicht in den SiGe-Plan. Es liest die neueste, nicht geloeschte
 * Planfassung und legt seine Bewertung DANEBEN ab — Zeitraeume, Gewerke und
 * Gefaehrdungen des Plans bleiben unberuehrt.
 */
export const KoordinierungModul: React.FC<Props> = ({
    projectId, projektName, projektNummer, projektAdresse, userProfile,
    company, quoteConfig, invoiceConfig, onBack, onKiBruecke, isReadOnly = false,
}) => {
    const k = useKoordinierung(projectId);

    const [reiter, setReiter] = useState<Reiter>('matrix');
    const [offenesPaar, setOffenesPaar] = useState<string | null>(null);
    const [filter, setFilter] = useState<'alle' | 'unbewertet' | 'veraltet'>('alle');
    const [meldung, setMeldung] = useState<string | null>(null);
    const [aktionsfehler, setAktionsfehler] = useState<string | null>(null);
    const [laeuft, setLaeuft] = useState(false);

    // Doppelklick-Riegel. `laeuft` als Zustandsvariable greift erst nach dem
    // naechsten Zeichnen — ein schneller zweiter Klick startet sonst einen
    // zweiten Lauf. Muster aus EmailModal, EinweisungModul und PlanungModul.
    const laufRef = useRef(false);

    const benutzerName = useMemo(() => {
        const p = userProfile || {};
        return [p.first_name, p.last_name].filter(Boolean).join(' ').trim() || p.email || '';
    }, [userProfile]);

    const zugriff: Schreibzugriff = { isReadOnly, benutzerName };

    const jetztIso = () => new Date().toISOString();

    /** Zweite Reihe des Schreibschutzes im Client: greift auch dann, wenn ein
     *  Knopf wider Erwarten aktiv ist oder `isReadOnly` zur Laufzeit kippt. */
    const abgelehntWennNurAnsicht = (was: string): boolean => {
        if (!isReadOnly) return false;
        setAktionsfehler(readOnlyMessage(was));
        return true;
    };

    const melde = (t: string) => {
        setMeldung(t);
        setAktionsfehler(null);
        window.setTimeout(() => setMeldung(m => (m === t ? null : m)), 6000);
    };

    const mitLauf = async (fn: () => Promise<void>) => {
        if (laufRef.current) return;
        laufRef.current = true;
        setLaeuft(true);
        setAktionsfehler(null);
        const freigeben = holdIdleExit('koordinierung-aktion');
        try {
            await fn();
        } catch (e) {
            setAktionsfehler(e instanceof Error ? e.message : String(e));
        } finally {
            freigeben();
            laufRef.current = false;
            setLaeuft(false);
        }
    };

    const sigeProjektId = k.quelle.sigeProject ? k.quelle.sigeProject.id : null;
    const kennzahlen = k.ergebnis.kennzahlen;

    const zeilen = useMemo(() => sortiereZeilen(k.ergebnis.zeilen), [k.ergebnis.zeilen]);
    const sichtbareZeilen = useMemo(() => filterZeilen(zeilen, {
        nurUnbewertet: filter === 'unbewertet',
        nurVeraltet: filter === 'veraltet',
    }), [zeilen, filter]);

    const aktiveZeile = useMemo(
        () => zeilen.find(z => z.schluessel === offenesPaar) || null,
        [zeilen, offenesPaar],
    );

    // ─── Schreibwege ─────────────────────────────────────────────────────

    const uebernehmeVorschlag = (zeile: AbgleichZeile, v: Vorschlag) => {
        if (abgelehntWennNurAnsicht('einen Vorschlag zu übernehmen')) return;
        void mitLauf(async () => {
            const neu = await createKoordinierungPaar(
                projectId, paarAusVorschlag(zeile, v, sigeProjektId), zugriff,
            );
            k.uebernehmePaar(neu);
            melde(`${v.code} übernommen — noch NICHT bestätigt. Erst Ihre Bestätigung macht daraus eine Festlegung.`);
        });
    };

    const eigeneGefaehrdung = (zeile: AbgleichZeile, gefaehrdung: string) => {
        if (abgelehntWennNurAnsicht('eine eigene Gefährdung einzutragen')) return;
        if (!gefaehrdung || !gefaehrdung.trim()) return;
        void mitLauf(async () => {
            const neu = await createKoordinierungPaar(projectId, leeresPaar(zeile, gefaehrdung), zugriff);
            k.uebernehmePaar(neu);
            melde('Eigene Gefährdung eingetragen.');
        });
    };

    const bestaetige = (paar: KoordinierungPaar) => {
        if (abgelehntWennNurAnsicht('eine Bewertung zu bestätigen')) return;
        void mitLauf(async () => {
            const stempel = jetztIso();
            const gesetzt = await bestaetigeKoordinierungPaar(paar.id, stempel, zugriff);
            if (gesetzt) {
                k.uebernehmePaar({ ...paar, bestaetigt_am: stempel, bestaetigt_von_name: benutzerName });
                melde('Bestätigt. Die Festlegung erscheint im Koordinierungsbericht.');
            } else {
                // Der Guard steht im UPDATE: war schon bestaetigt, bleibt das
                // ERSTE Datum stehen. Der Bestand wird nachgeladen, damit die
                // Anzeige nicht das falsche Datum behauptet.
                await k.neuLaden();
                melde('War bereits bestätigt — das erste Bestätigungsdatum bleibt stehen.');
            }
        });
    };

    const widerrufe = (paar: KoordinierungPaar) => {
        if (abgelehntWennNurAnsicht('eine Bestätigung zurückzunehmen')) return;
        void mitLauf(async () => {
            await widerrufeBestaetigung(paar.id, zugriff);
            k.uebernehmePaar({ ...paar, bestaetigt_am: null, bestaetigt_von_name: null });
            melde('Bestätigung zurückgenommen. Der Text der Bewertung bleibt stehen.');
        });
    };

    const loesche = (paar: KoordinierungPaar) => {
        if (abgelehntWennNurAnsicht('eine Bewertung zu löschen')) return;
        void mitLauf(async () => {
            const weg = await deleteKoordinierungPaar(paar.id, zugriff);
            if (weg) {
                k.entfernePaar(paar.id);
                melde('Bewertung gelöscht.');
            } else {
                await k.neuLaden();
                melde('Nicht gelöscht: bestätigte Bewertungen bleiben stehen. Nehmen Sie zuerst die Bestätigung zurück.');
            }
        });
    };

    const aendere = (paar: KoordinierungPaar, updates: Partial<KoordinierungPaar>) => {
        if (abgelehntWennNurAnsicht('eine Bewertung zu ändern')) return;
        void mitLauf(async () => {
            const neu = await updateKoordinierungPaar(paar.id, updates, zugriff);
            k.uebernehmePaar(neu);
        });
    };

    const fensterUebernehmen = (b: GespeicherteBewertung) => {
        if (abgelehntWennNurAnsicht('den geänderten Zeitraum zu übernehmen')) return;
        if (!b.neu) return;
        void mitLauf(async () => {
            const neu = await uebernimmNeuesFenster(b.zeile.id, b.neu, zugriff);
            k.uebernehmePaar(neu);
            melde('Zeitraum übernommen. Die Bestätigung ist entfallen — bestätigt war ein anderer Zeitraum.');
        });
    };

    /**
     * Kennzeichnet ueberholte Bewertungen.
     *
     * Ausdruecklich auf Knopfdruck und nicht beim Laden: das Kennzeichnen ist
     * ein Schreibvorgang mit Datum, und ein Datum, das beim blossen Ansehen
     * entsteht, sagt nichts aus. Geloescht wird dabei NICHTS — die Bewertung
     * ist der beweiskraeftige Teil, der Terminstand ist es nicht.
     */
    const kennzeichneVeraltet = () => {
        if (abgelehntWennNurAnsicht('überholte Bewertungen zu kennzeichnen')) return;
        const ids = k.ergebnis.zuVeralten;
        if (!ids.length) return;
        void mitLauf(async () => {
            const stempel = jetztIso();
            const anzahl = await markiereVeraltet(ids, stempel, zugriff);
            k.uebernehmeFelder(ids, { veraltet_seit: stempel });
            melde(`${anzahl} Bewertung(en) als überholt gekennzeichnet. Gelöscht wurde nichts.`);
        });
    };

    const hebeAuf = () => {
        if (abgelehntWennNurAnsicht('die Kennzeichnung „überholt" aufzuheben')) return;
        const ids = k.ergebnis.zuReaktivieren;
        if (!ids.length) return;
        void mitLauf(async () => {
            const anzahl = await hebeVeralterungAuf(ids, zugriff);
            k.uebernehmeFelder(ids, { veraltet_seit: null });
            melde(`${anzahl} Bewertung(en) gelten wieder — die Überschneidung ist zurück.`);
        });
    };

    /**
     * Der Koordinierungsbericht. Lesend, deshalb auch in Nur-Ansicht erlaubt.
     *
     * `heuteAlsIso()` statt `new Date().toISOString().slice(0, 10)`: letzteres
     * ist der UTC-Tag und traegt zwischen 00:00 und 02:00 MESZ den Vortag — im
     * Kopf des Berichts UND im Dateinamen. Ein Modul, das `Date` sonst ueberall
     * verbannt, darf sich den Stand nicht ueber die Zeitzone verschieben lassen.
     */
    const exportiere = () => {
        void mitLauf(async () => {
            const heute = heuteAlsIso();
            const pdfCompany = buildCompanyForPdf(
                company as never, quoteConfig as never, invoiceConfig as never,
            );
            const doc = generateKoordinierungPdf(
                {
                    gewerke: k.gewerke,
                    matrix: k.matrix,
                    zeilen,
                    ohneZeitraum: k.ohneZeitraum,
                    offeneGewerke: k.offeneGewerke,
                    hinweise: k.hinweise,
                    kennzahlen,
                    katalogVersion: aktuelleKatalogVersion(),
                    planStand: k.quelle.sigeProject
                        ? (k.quelle.sigeProject.version_label || k.quelle.sigeProject.plan_nr || '')
                        : '',
                },
                pdfCompany,
                {
                    projektName,
                    projektNummer,
                    bauvorhabenAdresse: projektAdresse,
                    datumIso: heute,
                },
            );
            doc.save(koordinierungDateiname(projektName || '', heute));
            melde('Koordinierungsbericht erzeugt.');
        });
    };

    // ─── KI-Assistent ────────────────────────────────────────────────────

    /** Welche Gewerke der Regelkatalog kennt — Teil des Kontexts, damit die
     *  KI weiss, wo der Katalog strukturell schweigt. */
    const zugeordneteGewerke = useMemo(
        () => new Set(k.gewerke.filter(g => g.zugeordnet).map(g => g.norm)),
        [k.gewerke],
    );

    /**
     * Die Innereien der Bruecke, nach jedem Zeichnen frisch.
     *
     * ═══ WARUM UEBER EINE REF UND NICHT ALS PROP-WERT ══════════════════════
     * Die Bruecke selbst muss DIESELBE bleiben: die Huelle legt sie in ihren
     * Zustand, und ein bei jedem Zeichnen neues Objekt meldete sich in einer
     * Endlosschleife neu an. Ihr Inhalt darf dagegen nicht einfrieren — sonst
     * baute der Assistent den Kontext aus der Lage von vorhin und schriebe mit
     * einer Freigabe, die inzwischen entzogen ist.
     */
    const kiStand = useRef<{
        oeffne: () => Promise<KiOeffnung>;
        uebernimm: (auswahl: KiUebernahme) => Promise<string>;
    } | null>(null);

    useEffect(() => {
        const plan = k.quelle.sigeProject;
        const sperrgrund = isReadOnly
            ? readOnlyTitle('Vorschläge des Assistenten übernehmen')
            : k.tabelleFehlt
                ? 'Die Ablage für Bewertungen fehlt in dieser Umgebung noch (Migration Nr. 40). '
                    + 'Fragen geht, übernehmen nicht.'
                : null;

        kiStand.current = {
            oeffne: async () => {
                // Ein Kontext aus einer halb geladenen Lage saehe aus wie ein
                // Projekt ohne Ueberschneidungen — die KI antwortete dann
                // ausfuehrlich auf eine falsche Frage.
                if (k.laden) {
                    throw new Error('Die Lage wird noch geladen. Bitte gleich noch einmal öffnen.');
                }
                // Die Planungsphase geht als Baustein mit: ob der Betrieb
                // weiterlaeuft oder im oeffentlichen Verkehrsraum gearbeitet
                // wird, aendert, was ZWISCHEN den Gewerken zu regeln ist.
                // Fail-open — ohne sie `planung: null`. Der einzige Grund, warum
                // `oeffne` hier asynchron ist.
                const planung = await ladePlanungsphaseFuerKi(projectId);
                return {
                    paket: {
                        art: 'koordinierung',
                        // Der Kontext wird HIER gebaut und nicht auf Vorrat: es geht
                        // die Lage mit, die der Anwender gerade vor sich hat.
                        kontext: baueKoordinierungKontext(
                            {
                                bauvorhaben: plan?.baumassnahme_name || projektName,
                                bauzeitVon: plan?.zeitraum_von,
                                bauzeitBis: plan?.zeitraum_bis,
                                hauptabschnitte: plan?.hauptabschnitte,
                            },
                            zeilen.map(z => ({
                                aLabel: z.aLabel,
                                bLabel: z.bLabel,
                                aZugeordnet: zugeordneteGewerke.has(z.aNorm),
                                bZugeordnet: zugeordneteGewerke.has(z.bNorm),
                                von: z.berechnet ? z.berechnet.von : null,
                                bis: z.berechnet ? z.berechnet.bis : null,
                                dauerTage: z.berechnet ? z.berechnet.dauerTage : null,
                                // Was der Katalog schon sagt, geht als „schon gesagt"
                                // mit — die KI soll ERGAENZEN, nicht wiederholen.
                                katalogVorschlaege: z.vorschlaege.map(v => ({
                                    code: v.code,
                                    gefaehrdung: v.gefaehrdung,
                                    massnahme: v.massnahme,
                                    prioritaet: v.prioritaet,
                                })),
                            })),
                            k.offeneGewerke.map(g => ({
                                label: g.label,
                                anzahlSchritte: g.anzahlSchritte,
                                von: g.von,
                                bis: g.bis,
                            })),
                            aktuelleKatalogVersion(),
                            planung,
                        ),
                        // Grundlage der Doppelerkennung: gespeicherte Bewertungen UND
                        // Katalogvorschlaege je Paar.
                        zeilen: zeilen.map(z => ({
                            aNorm: z.aNorm,
                            aLabel: z.aLabel,
                            bNorm: z.bNorm,
                            bLabel: z.bLabel,
                            berechnet: z.berechnet
                                ? { von: z.berechnet.von, bis: z.berechnet.bis, dauerTage: z.berechnet.dauerTage }
                                : null,
                            gespeicherteGefaehrdungen: z.bewertungen.map(b => b.zeile.gefaehrdung),
                            katalogGefaehrdungen: z.vorschlaege.map(v => v.gefaehrdung),
                        })),
                        sigeProjektId,
                    },
                    darfUebernehmen: !isReadOnly && !k.tabelleFehlt,
                    sperrgrund,
                };
            },

            /**
             * Legt die angenommenen Vorschlaege an — ueber denselben Dienst wie
             * ein Katalogtreffer, mit `quelle: 'ki'` und OHNE Bestaetigung.
             *
             * Zweite Reihe des Schreibschutzes: die Sperre wird hier noch einmal
             * geprueft. Das Fenster hat sie beim Oeffnen gelesen, und `isReadOnly`
             * kann zur Laufzeit kippen, wenn die Projektsperre an einen anderen
             * Benutzer geht.
             */
            uebernimm: async (auswahl: KiUebernahme) => {
                if (auswahl.art !== 'koordinierung') {
                    throw new Error('Dieser Reiter übernimmt nur Bewertungen zu Gewerkepaaren.');
                }
                if (isReadOnly) {
                    throw new Error(readOnlyMessage('einen KI-Vorschlag zu übernehmen'));
                }
                if (k.tabelleFehlt) {
                    throw new Error('Die Ablage für Bewertungen fehlt in dieser Umgebung noch '
                        + '(Migration Nr. 40). Es wurde nichts angelegt.');
                }
                const freigeben = holdIdleExit('koordinierung-ki');
                try {
                    let angelegt = 0;
                    // Nacheinander und nicht nebeneinander: bricht eine Zeile ab,
                    // sollen die vorherigen stehen und die Meldung stimmen.
                    for (const entwurf of auswahl.entwuerfe) {
                        k.uebernehmePaar(await createKoordinierungPaar(projectId, entwurf, zugriff));
                        angelegt += 1;
                    }
                    const satz = `${angelegt} KI-Vorschlag/Vorschläge übernommen — Herkunft „KI", `
                        + 'noch NICHT bestätigt. Erst Ihre Bestätigung macht daraus eine Festlegung.';
                    melde(satz);
                    return satz;
                } finally {
                    freigeben();
                }
            },
        };
    });

    /** Bleibt ueber die Lebensdauer des Moduls DIESELBE (siehe `kiStand`). */
    const kiBruecke = useMemo<KiBruecke>(() => ({
        oeffne: async () => {
            if (!kiStand.current) throw new Error('Die Lage ist noch nicht geladen.');
            return kiStand.current.oeffne();
        },
        uebernimm: async (auswahl) => {
            if (!kiStand.current) throw new Error('Die Lage ist noch nicht geladen.');
            return kiStand.current.uebernimm(auswahl);
        },
    }), []);

    useEffect(() => {
        onKiBruecke?.(kiBruecke);
        // Abmelden beim Verlassen: sonst zeigte das Fenster die Lage eines
        // Reiters, der gar nicht mehr steht.
        return () => onKiBruecke?.(null);
    }, [onKiBruecke, kiBruecke]);

    // ─── Anzeige ─────────────────────────────────────────────────────────

    const REITER: { key: Reiter; label: string; icon: React.ReactNode; zahl?: number }[] = [
        { key: 'matrix', label: 'Matrix', icon: <Grid3x3 className="w-4 h-4" /> },
        { key: 'zeitachse', label: 'Zeitachse', icon: <CalendarRange className="w-4 h-4" /> },
        { key: 'liste', label: 'Liste', icon: <List className="w-4 h-4" />, zahl: kennzahlen.paare },
        {
            key: 'gewerke',
            label: 'Gewerke',
            icon: <HelpCircle className="w-4 h-4" />,
            // Auch die nur teilweise terminierten Gewerke zaehlen mit: sie
            // stehen in der Matrix und sehen dort vollstaendig aus — ohne die
            // Zahl am Reiter faende sie niemand.
            zahl: kennzahlen.nichtZugeordnet + kennzahlen.gewerkeOhneZeitraum + kennzahlen.gewerkeMitLuecke,
        },
        { key: 'dokumente', label: 'Dokumente', icon: <FolderOpen className="w-4 h-4" /> },
    ];

    const STATUS_ETIKETT: Record<PaarStatus, { text: string; klasse: string }> = {
        unbewertet: { text: 'unbewertet', klasse: 'bg-slate-800 text-white' },
        bewertet: { text: 'bewertet', klasse: 'bg-emerald-100 text-emerald-800' },
        geaendert: { text: 'Zeitraum verschoben', klasse: 'bg-blue-100 text-blue-800' },
        veraltet: { text: 'überholt', klasse: 'bg-slate-200 text-slate-600' },
    };

    const PRIO_ETIKETT: Record<Prioritaet, string> = {
        hoch: 'text-rose-600',
        mittel: 'text-amber-600',
        niedrig: 'text-slate-500',
    };

    return (
        // `min-h-0` an beiden Ebenen: ein Flex-Kind hat `min-height: auto` und
        // schrumpft nicht unter seinen Inhalt, dann greift `overflow-y-auto`
        // nicht (dieselbe Klemme wie im Planungsmodul).
        <div data-glass-view="module-koordinierung" data-glass-bereich="module" className="h-full flex flex-col bg-slate-50 min-h-0">
            <div className="flex-1 overflow-y-auto min-h-0">
                <div className="max-w-6xl mx-auto px-4 py-6">

                    {/* ─── Kopf ──────────────────────────────────────── */}
                    <div data-glass="titel" className="flex items-start gap-4 mb-6">
                        <button
                            type="button"
                            onClick={onBack}
                            className="mt-1 p-2 rounded-lg hover:bg-slate-100 text-slate-500 shrink-0"
                            aria-label="Zurück"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div className="min-w-0 flex-1">
                            <h1 className="text-2xl font-black text-slate-900 leading-tight">Gewerke-Überschneidung</h1>
                            <p className="text-sm text-slate-500">
                                {projektName || 'Projekt'}
                                {projektNummer && <> · {projektNummer}</>}
                                {' '}· Koordinierung nach § 3 Abs. 3 BaustellV · Katalogfassung {aktuelleKatalogVersion()}
                            </p>
                        </div>
                        <button
                            type="button"
                            onClick={exportiere}
                            disabled={laeuft || kennzahlen.gewerke === 0}
                            className="mt-1 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40 shrink-0"
                        >
                            <Download className="w-3.5 h-3.5" /> Bericht
                        </button>
                    </div>

                    {isReadOnly && (
                        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2.5 text-sm text-amber-800">
                            {readOnlyTitle('Bewertungen')}
                        </div>
                    )}

                    {/* Fail-open: die Kachel lebt, auch wenn Migration Nr. 40
                        noch nicht gefahren ist — die Analyse rechnet, nur
                        speichern kann sie nicht. Das muss dastehen, sonst sieht
                        es aus wie „noch nichts bewertet". */}
                    {k.tabelleFehlt && (
                        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>
                                Die Ablage für Bewertungen fehlt in dieser Umgebung noch (Migration Nr. 40).
                                Analyse, Matrix, Zeitachse und Bericht arbeiten; Bewertungen lassen sich noch
                                nicht speichern.
                            </span>
                        </div>
                    )}

                    {/* ─── Kennzahlen ────────────────────────────────── */}
                    {kennzahlen.gewerke > 0 && (
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                            {[
                                { wert: kennzahlen.gewerke, label: 'Gewerke', farbe: 'text-slate-800' },
                                { wert: kennzahlen.paare, label: 'Paare', farbe: 'text-slate-800' },
                                { wert: kennzahlen.unbewertet, label: 'unbewertet', farbe: 'text-rose-600' },
                                { wert: kennzahlen.bestaetigt, label: 'bestätigt', farbe: 'text-emerald-600' },
                                { wert: kennzahlen.veraltet, label: 'überholt', farbe: 'text-slate-500' },
                            ].map(z => (
                                <div key={z.label} className="bg-white rounded-xl border border-slate-200 px-4 py-3">
                                    <div className={`text-2xl font-black ${z.farbe}`}>{z.wert}</div>
                                    <div className="text-[11px] text-slate-400 font-bold uppercase tracking-wide">{z.label}</div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ─── Meldungen ─────────────────────────────────── */}
                    {k.fehler && (
                        <div className="mb-4 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800">
                            Die Koordinierung konnte nicht geladen werden: {k.fehler}
                        </div>
                    )}
                    {aktionsfehler && (
                        <div className="mb-4 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 text-sm text-rose-800 flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                            <span className="flex-1 whitespace-pre-line">{aktionsfehler}</span>
                            <button type="button" onClick={() => setAktionsfehler(null)} className="text-xs font-bold underline shrink-0">Schließen</button>
                        </div>
                    )}
                    {meldung && (
                        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800 flex items-start gap-2">
                            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                            <span>{meldung}</span>
                        </div>
                    )}

                    {/* ─── Überholte Bewertungen ─────────────────────── */}
                    {k.ergebnis.zuVeralten.length > 0 && (
                        <div className="mb-4 bg-slate-100 border border-slate-300 rounded-xl px-4 py-3 text-sm text-slate-800 flex items-center gap-3 flex-wrap">
                            <Clock className="w-4 h-4 text-slate-500 shrink-0" />
                            <span className="flex-1">
                                {k.ergebnis.zuVeralten.length} Bewertung(en) beziehen sich auf Überschneidungen,
                                die der Plan nicht mehr hergibt. Sie werden gekennzeichnet, nicht gelöscht.
                            </span>
                            <button
                                type="button"
                                onClick={kennzeichneVeraltet}
                                disabled={isReadOnly || laeuft || k.tabelleFehlt}
                                title={isReadOnly ? readOnlyTitle('Kennzeichnen') : undefined}
                                className="text-xs font-bold px-3 py-1.5 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                            >
                                Als überholt kennzeichnen
                            </button>
                        </div>
                    )}
                    {k.ergebnis.zuReaktivieren.length > 0 && (
                        <div className="mb-4 bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-900 flex items-center gap-3 flex-wrap">
                            <RefreshCw className="w-4 h-4 shrink-0" />
                            <span className="flex-1">
                                {k.ergebnis.zuReaktivieren.length} als überholt gekennzeichnete Bewertung(en)
                                überschneiden sich wieder.
                            </span>
                            <button
                                type="button"
                                onClick={hebeAuf}
                                disabled={isReadOnly || laeuft || k.tabelleFehlt}
                                title={isReadOnly ? readOnlyTitle('Kennzeichnen') : undefined}
                                className="text-xs font-bold px-3 py-1.5 bg-white border border-emerald-300 rounded-lg hover:bg-emerald-50 disabled:opacity-40"
                            >
                                Kennzeichnung aufheben
                            </button>
                        </div>
                    )}

                    {/* ─── Allgemeine Regeln ─────────────────────────── */}
                    {k.hinweise.length > 0 && (
                        <div className="mb-6 bg-white border border-slate-200 rounded-2xl p-4 space-y-2.5">
                            <p className="text-[11px] font-black text-slate-400 uppercase tracking-wide">
                                Was allein aus der Gleichzeitigkeit folgt
                            </p>
                            {k.hinweise.map(h => (
                                <div key={h.regel.code} className="text-xs text-slate-700">
                                    <span className="font-black text-slate-400 mr-1.5">{h.regel.code}</span>
                                    <span className="font-bold">{h.regel.hinweis}</span>
                                    <span className="text-slate-500"> {h.begruendung}</span>
                                    <p className="text-slate-500 mt-0.5">{h.regel.massnahme}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ─── Reiter ────────────────────────────────────── */}
                    <div data-glass="reiter" className="flex items-center gap-1 border-b border-slate-200 mb-6 flex-wrap">
                        {REITER.map(r => (
                            <button
                                key={r.key}
                                type="button"
                                onClick={() => setReiter(r.key)}
                                className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 -mb-px whitespace-nowrap transition-colors ${
                                    reiter === r.key
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-slate-500 hover:text-slate-700'
                                }`}
                            >
                                {r.icon} {r.label}
                                {typeof r.zahl === 'number' && r.zahl > 0 && (
                                    <span className="text-[10px] font-black bg-slate-100 text-slate-500 rounded-full px-1.5 py-0.5">{r.zahl}</span>
                                )}
                            </button>
                        ))}
                    </div>

                    {k.laden && <p className="text-sm text-slate-400 py-10 text-center">Wird geladen …</p>}

                    {!k.laden && !k.quelle.sigeProject && reiter !== 'dokumente' && (
                        <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center mb-6">
                            <p className="text-sm text-slate-500">
                                Für dieses Projekt gibt es noch keinen SiGe-Plan. Die Überschneidungsanalyse
                                entsteht aus den Arbeitsschritten der neuesten Planfassung — legen Sie den Plan
                                an, dann steht sie hier.
                            </p>
                        </div>
                    )}

                    {/* ─── Matrix ────────────────────────────────────── */}
                    {!k.laden && reiter === 'matrix' && (
                        <div className="space-y-4">
                            <MatrixAnsicht
                                matrix={k.matrix}
                                ohneZeitraum={k.ohneZeitraum}
                                offeneGewerke={k.offeneGewerke}
                                aktiv={offenesPaar}
                                onZelle={s => setOffenesPaar(s)}
                            />
                            {aktiveZeile && (
                                <PaarDetail
                                    zeile={aktiveZeile}
                                    isReadOnly={isReadOnly}
                                    laeuft={laeuft}
                                    tabelleFehlt={k.tabelleFehlt}
                                    onSchliessen={() => setOffenesPaar(null)}
                                    onUebernehmen={uebernehmeVorschlag}
                                    onEigene={eigeneGefaehrdung}
                                    onBestaetigen={bestaetige}
                                    onWiderrufen={widerrufe}
                                    onLoeschen={loesche}
                                    onAendern={aendere}
                                    onFensterUebernehmen={fensterUebernehmen}
                                />
                            )}
                        </div>
                    )}

                    {/* ─── Zeitachse ─────────────────────────────────── */}
                    {!k.laden && reiter === 'zeitachse' && (
                        <ZeitachseAnsicht
                            zeitachse={k.zeitachse}
                            ohneZeitraum={k.ohneZeitraum}
                            hervorgehoben={aktiveZeile ? [aktiveZeile.aNorm, aktiveZeile.bNorm] : []}
                        />
                    )}

                    {/* ─── Liste ─────────────────────────────────────── */}
                    {!k.laden && reiter === 'liste' && (
                        <div className="space-y-4">
                            <div className="bg-white rounded-2xl border border-slate-200 p-3 flex items-center gap-2 flex-wrap">
                                <span className="text-[11px] font-black text-slate-400 uppercase tracking-wide mr-1">Filter</span>
                                {([
                                    { key: 'alle', label: `Alle (${kennzahlen.paare})` },
                                    { key: 'unbewertet', label: `Unbewertet (${kennzahlen.unbewertet})` },
                                    { key: 'veraltet', label: `Überholt (${kennzahlen.veraltet})` },
                                ] as const).map(f => (
                                    <button
                                        key={f.key}
                                        type="button"
                                        onClick={() => setFilter(f.key)}
                                        className={`px-3 py-1.5 text-xs font-bold rounded-lg border ${
                                            filter === f.key
                                                ? 'bg-slate-800 text-white border-slate-800'
                                                : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                                        }`}
                                    >
                                        {f.label}
                                    </button>
                                ))}
                                <span className="ml-auto text-[11px] text-slate-400">
                                    Sortiert nach Schwere, dann nach Dauer.
                                </span>
                            </div>

                            {sichtbareZeilen.length === 0 && (
                                <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-sm text-slate-500">
                                    {/* Plan ohne Zeitachse (Bauzeitenplan liegt noch nicht vor): ohne Daten
                                        gibt es nichts zu überschneiden — das ist kein Pflegefehler. */}
                                    {kennzahlen.paare === 0 && k.quelle.sigeProject && !planHatZeitachse(k.quelle.sigeProject.darstellung)
                                        ? 'Plan ohne Bauzeiten – Überschneidungen ergeben sich erst mit dem Bauzeitenplan.'
                                        : kennzahlen.paare === 0
                                        ? 'Keine zeitliche Überschneidung zwischen den Gewerken des Plans. Prüfen Sie im Reiter „Gewerke", ob allen ARBEITSSCHRITTEN ein Zeitraum hinterlegt ist — auch ein einzelner Schritt ohne Datum fällt aus der Rechnung, und dann entsteht der Eindruck, es gäbe keine Überschneidung.'
                                        : 'Kein Paar passt zu diesem Filter.'}
                                </div>
                            )}

                            {sichtbareZeilen.map(z => (
                                <div key={z.schluessel}>
                                    <button
                                        type="button"
                                        onClick={() => setOffenesPaar(offenesPaar === z.schluessel ? null : z.schluessel)}
                                        className={`w-full text-left bg-white rounded-2xl border p-4 hover:border-slate-300 transition-colors ${
                                            offenesPaar === z.schluessel ? 'border-slate-400' : 'border-slate-200'
                                        }`}
                                    >
                                        <div className="flex items-start gap-3 flex-wrap">
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-black text-slate-900">
                                                    {z.aLabel} <span className="text-slate-300 mx-1">×</span> {z.bLabel}
                                                </p>
                                                <p className="text-xs text-slate-500 mt-0.5">
                                                    {z.berechnet
                                                        ? `${z.dauerTage} gemeinsame Tag(e) · ${z.berechnet.von} bis ${z.berechnet.bis}`
                                                        : 'keine gemeinsame Zeit mehr'}
                                                    {z.bewertungen.length > 0 && ` · ${z.bewertungen.length} Bewertung(en), ${z.bestaetigt} bestätigt`}
                                                </p>
                                            </div>
                                            {z.hoechstePrioritaet && (
                                                <span className={`text-[11px] font-black uppercase tracking-wide ${PRIO_ETIKETT[z.hoechstePrioritaet]}`}>
                                                    {z.hoechstePrioritaet}
                                                </span>
                                            )}
                                            <span className={`text-[10px] font-black uppercase tracking-wide rounded px-2 py-1 ${STATUS_ETIKETT[z.status].klasse}`}>
                                                {STATUS_ETIKETT[z.status].text}
                                            </span>
                                        </div>
                                    </button>
                                    {offenesPaar === z.schluessel && (
                                        <div className="mt-3">
                                            <PaarDetail
                                                zeile={z}
                                                isReadOnly={isReadOnly}
                                                laeuft={laeuft}
                                                tabelleFehlt={k.tabelleFehlt}
                                                onSchliessen={() => setOffenesPaar(null)}
                                                onUebernehmen={uebernehmeVorschlag}
                                                onEigene={eigeneGefaehrdung}
                                                onBestaetigen={bestaetige}
                                                onWiderrufen={widerrufe}
                                                onLoeschen={loesche}
                                                onAendern={aendere}
                                                onFensterUebernehmen={fensterUebernehmen}
                                            />
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ─── Gewerke ──────────────────────────────────── */}
                    {!k.laden && reiter === 'gewerke' && (
                        <GewerkZuordnung
                            gewerke={k.gewerke}
                            offeneGewerke={k.offeneGewerke}
                            ohneZeitraum={k.ohneZeitraum}
                            zuordnungen={k.zuordnungen}
                            isReadOnly={isReadOnly}
                            onZuordnen={k.setzeZuordnung}
                        />
                    )}

                    {/* ─── Dokumente ────────────────────────────────── */}
                    {/* Die Terminpläne der Firmen liegen in der Projektablage.
                        Sie werden hier eingebettet und nicht kopiert: ein
                        Dokument liegt genau einmal im Projekt. */}
                    {reiter === 'dokumente' && (
                        <DokumentePanel
                            projectId={projectId}
                            modul="koordinierung"
                            userProfile={userProfile}
                            isReadOnly={isReadOnly}
                            onSchliessen={() => setReiter('matrix')}
                        />
                    )}

                </div>
            </div>
        </div>
    );
};
